/**
 * LeaguePage — Full roster & available players viewer
 *
 * Route: /league/:id/roster  (default)
 *
 * Standard layout: AppShell → max-w-4xl mx-auto px-6 py-8
 */

import { statusDisplay } from '../components/JaiV2'
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import PlayerPhoto from '../components/PlayerPhoto'

export default function LeaguePage({ tab: initialTab = 'roster' }) {
  const { id } = useParams()

  const [league,    setLeague]    = useState(null)
  const [roster,    setRoster]    = useState([])
  const [players,   setPlayers]   = useState([])
  const [activeTab, setActiveTab] = useState(initialTab)
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState('')

  useEffect(() => {
    loadLeague()
  }, [id])

  const loadLeague = async () => {
    setLoading(true)
    setError('')
    const [leagueRes, rosterRes] = await Promise.allSettled([
      leaguesApi.get(id),
      leaguesApi.getRoster(id),
    ])
    if (leagueRes.status === 'fulfilled') setLeague(leagueRes.value.data.league)
    if (rosterRes.status === 'fulfilled') setRoster(rosterRes.value.data.players || [])
    if (leagueRes.status === 'rejected' && rosterRes.status === 'rejected') {
      setError('Failed to load league data')
    }
    setLoading(false)
  }

  const loadPlayers = async () => {
    if (players.length > 0) return
    try {
      const res = await leaguesApi.getPlayers(id, { limit: 30 })
      setPlayers(res.data.players || [])
    } catch (err) {
      console.error('Failed to load players:', err)
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    if (tab === 'players') loadPlayers()
  }

  // ── Loading / error states ────────────────────────────────────────────────────

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-700 text-sm">Loading…</div>
        </div>
      </AppShell>
    )
  }

  if (error) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-red-600 mb-3 text-sm">{error}</p>
            <button onClick={loadLeague} className="text-blue-700 text-sm hover:underline">Try again</button>
          </div>
        </div>
      </AppShell>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-8 py-8">

        {/* Back link */}
        <Link
          to={`/league/${id}`}
          className="text-gray-800 hover:text-gray-900 text-sm transition mb-6 inline-flex items-center gap-1"
        >
          ← {league?.name ?? 'League'}
        </Link>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🏀</span>
            <h1 className="text-2xl font-bold text-gray-900">{league?.name}</h1>
          </div>
          {league?.teamName && (
            <p className="text-gray-700 text-sm ml-9 mt-0.5">{league.teamName}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap ml-9 mt-1">
            {league?.season && <span className="text-gray-800 text-sm">{league.season}</span>}
            {league?.teamCount > 0 && (
              <>
                <span className="text-gray-700 text-sm">·</span>
                <span className="text-gray-800 text-sm">{league.teamCount} teams</span>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-200 mb-6 w-fit">
          {[
            { id: 'roster',  label: `My Roster (${roster.length})` },
            { id: 'players', label: 'Available Players' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-blue-700 text-white'
                  : 'text-gray-800 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'roster' && (
          <PlayerTable players={roster} emptyMessage="No roster data. Try syncing your ESPN league." />
        )}
        {activeTab === 'players' && (
          <PlayerTable players={players} emptyMessage="Loading available players…" />
        )}
      </div>
    </AppShell>
  )
}

// ── PlayerTable ───────────────────────────────────────────────────────────────

function PlayerTable({ players, emptyMessage }) {
  if (players.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
        <p className="text-gray-700 text-sm">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(10,31,68,0.08)' }}>
      <table className="w-full">
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(10,31,68,0.07)' }}>
            <th className="text-left px-5 py-3" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(10,31,68,0.4)', textTransform: 'uppercase', letterSpacing: '0.10em' }}>Player</th>
            <th className="text-left px-5 py-3" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(10,31,68,0.4)', textTransform: 'uppercase', letterSpacing: '0.10em' }}>Status</th>
            <th className="text-right px-5 py-3" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(10,31,68,0.4)', textTransform: 'uppercase', letterSpacing: '0.10em' }}>% Owned</th>
          </tr>
        </thead>
        <tbody>
          {players.map((player, i) => (
            <tr
              key={player.id || i}
              style={{ borderBottom: i < players.length - 1 ? '1px solid rgba(10,31,68,0.05)' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F6F8FB'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                  <PlayerPhoto
                    name={player.name}
                    size={36}
                    rounded="sm"
                  />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0A1F44' }}>{player.name}</div>
                    {player.position && (
                      <div style={{ fontSize: 11, color: 'rgba(10,31,68,0.4)', marginTop: 1 }}>{player.position}</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-5 py-3">
                <StatusBadge status={player.injuryStatus} />
              </td>
              <td className="px-5 py-3 text-right" style={{ fontSize: 13, color: 'rgba(10,31,68,0.5)' }}>
                {player.percentOwned?.toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const shown = statusDisplay(status)
  status = shown.key
  const styles = {
    ACTIVE:       'text-green-700',
    OUT:          'text-red-600',
    GTD:          'text-amber-700',
    IR:           'text-red-600',
    QUESTIONABLE: 'text-amber-700',
  }
  return (
    <span className={`inline-block py-0.5 text-xs font-semibold ${styles[status] || 'text-gray-700'}`}>
      {shown.label}
    </span>
  )
}
